import { Link } from "react-router-dom";
import { Film, Building2, Layers, Truck, Landmark, Clapperboard, Megaphone, Tv, Sparkles, ArrowRight, ShieldCheck, Newspaper, Mic2, Users, UserSquare2, Send, Music2, Boxes, Drama, Theater } from "lucide-react";
import OnboardBackButton from "@/components/OnboardBackButton";

const ORGS = [
  {
    key: "association",
    label: "Trade Organization",
    layer: "L1",
    blurb: "Producers' / Directors' / Cinematographers' associations — represent your members, verify industry credentials, govern via committee with multi-party OTP guardrail.",
    icon: ShieldCheck,
    accent: "#6B4EFF",
    cta: "Register Trade Org",
    href: "/onboard/trade-org",
    enabled: true,
  },
  {
    key: "advertising_agency",
    label: "Advertising Agency",
    layer: "L2",
    blurb: "Creative · OOH · Digital · Media Buying · Influencer · Activation — pick any combination. Six agency archetypes in one shell, each with its own office floor.",
    icon: Megaphone,
    accent: "#F59E0B",
    cta: "Choose Agency Type",
    href: "/onboard/agency-type",
    enabled: true,
  },
  {
    key: "production_house",
    label: "Production House",
    layer: "L3",
    blurb: "Studios that originate projects — film, ad, TV, web series. Create projects, issue call sheets, manage crew at scale.",
    icon: Film,
    accent: "#D9383A",
    cta: "Set up Production House",
    href: "/onboard/production-house",
    enabled: true,
  },
  {
    key: "theater_group",
    label: "Theater Group",
    layer: "L17",
    blurb: "Repertory ensembles, folk troupes, comedy collectives — originate plays and stage shows. Rehearsal calendars, cast lists, and hall bookings, all in one place.",
    icon: Drama,
    accent: "#F97316",
    cta: "Set up Theater Group",
    href: "/onboard/theater-group",
    enabled: true,
  },
  {
    key: "studio",
    label: "Studio Facility",
    layer: "L4",
    blurb: "Shooting floors · standing sets · dubbing · foley · ADR · post & VFX rooms. Pick facilities you offer, schedule rooms, manage staff with role-graded access.",
    icon: Building2,
    accent: "#F5B82E",
    cta: "Set up Studio Facility",
    href: "/onboard/studio",
    enabled: true,
  },
  {
    key: "theater_hall",
    label: "Theater Hall / Venue",
    layer: "L18",
    blurb: "Proscenium, thrust, black-box, open-air amphitheatre — list your hall so theater groups and event producers can book you online.",
    icon: Theater,
    accent: "#7C3AED",
    cta: "Set up Theater Hall",
    href: "/onboard/theater-hall",
    enabled: true,
  },
  {
    key: "studio_network",
    label: "Studio · Network · Streamer",
    layer: "L5",
    blurb: "Macro slates · IP management · distribution timelines. For broadcasters, streamers, and majors (Warner Bros, Netflix, Disney) commissioning and green-lighting slates.",
    icon: Tv,
    accent: "#0EA5E9",
    cta: "Set up Slate Platform",
    href: "/onboard/studio-network",
    enabled: true,
  },
  {
    key: "event_production",
    label: "Event Production",
    layer: "L6",
    blurb: "Live experiences · experiential marketing · physical activations. Concerts, festivals, brand activations — separate ops workflows from film sets.",
    icon: Sparkles,
    accent: "#EC4899",
    cta: "Set up Event Production",
    href: "/onboard/event-production",
    enabled: true,
  },
  {
    key: "vendor",
    label: "Vendor",
    layer: "L7",
    blurb: "Camera rental · lighting · transport · catering · post services. List your categories (29 to pick from) or the new Layer-7 taxonomy — get verified, receive bookings.",
    icon: Truck,
    accent: "#4A4E51",
    cta: "Set up Vendor",
    href: "/onboard/vendor",
    enabled: true,
  },
  {
    key: "investor",
    label: "Investor · Banker · Financier",
    layer: "L8",
    blurb: "Bankers · HNIs · VCs · OTT commissioning studios · corporates. Initiate projects, hire executing producers, monitor read-only financier views on projects you back.",
    icon: Landmark,
    accent: "#F5B82E",
    cta: "Set up Investor",
    href: "/onboard/investor",
    enabled: true,
  },
  {
    key: "pr_agency",
    label: "PR Agency",
    layer: "L9",
    blurb: "Media relations · press junkets · sponsor journalists. Approve journalists who nominate your agency as their verification sponsor.",
    icon: Newspaper,
    accent: "#22D3EE",
    cta: "Set up PR Agency",
    href: "/onboard/pr-agency",
    enabled: true,
  },
  {
    key: "journalist",
    label: "Journalist / Paparazzi / Influencers",
    layer: "L10",
    blurb: "Reporters, film critics, digital creators, YouTubers and paparazzi. Verify via an association, three peer references, or a sponsoring PR agency — or self-identify without a formal body.",
    icon: Mic2,
    accent: "#22D3EE",
    cta: "Register as Journalist / Influencer",
    href: "/onboard/journalist",
    enabled: true,
  },
  {
    key: "casting_agency",
    label: "Casting / Talent Management",
    layer: "L11",
    blurb: "Two related but distinct businesses on one profile shell — Casting Agencies (Mukesh Chhabra, Casting Bay) that hire talent for a role, and Talent Management Agencies (KWAN, Matrix, YRF Talent) that represent talent long-term. Pick your model on the next screen.",
    icon: Users,
    accent: "#A78BFA",
    cta: "Set up Casting / Talent Agency",
    href: "/onboard/casting-agency",
    enabled: true,
  },
  {
    key: "talent_model",
    label: "Talent / Model",
    layer: "L12",
    blurb: "Actors · models · performing artists. Verify via a trade association, three peer references, or a sponsoring casting agency.",
    icon: UserSquare2,
    accent: "#A78BFA",
    cta: "Register as Talent",
    href: "/onboard/talent-model",
    enabled: true,
  },
  {
    key: "distributor",
    label: "Distributor / Sales Agent",
    layer: "L13",
    blurb: "Two related businesses on one profile shell — Distributors (PVR Pictures, YRF, T-Series) that own rights in a territory + window, and Sales Agents (FilmSharks, Reel Suspects) that pitch a slate to distributors globally. Pick your model on the next screen.",
    icon: Send,
    accent: "#0F766E",
    cta: "Choose Distributor / Sales Agent",
    href: "/onboard/distributor",
    enabled: true,
  },
  {
    key: "exhibitor",
    label: "Exhibitor / Cinema Chain",
    layer: "L14",
    blurb: "Multiplex chains · mini-multiplexes · single-screens · drive-ins. Add every property you operate — producers and distributors can now find your inventory.",
    icon: Film,
    accent: "#DC2626",
    cta: "Set up Exhibitor",
    href: "/onboard/exhibitor",
    enabled: true,
  },
  {
    key: "music_label",
    label: "Music Label / Publisher",
    layer: "L15",
    blurb: "Recording labels (T-Series, Sony Music, YRF Music) that acquire film soundtracks + master rights, and Music Publishers that own composition/lyric rights. India often bundles both; global markets keep them split. ISRC · ISWC · PRO affiliations · sync-licensing built in.",
    icon: Music2,
    accent: "#E11D74",
    cta: "Reserved · Coming Soon",
    href: "/onboard/music-label",
    enabled: false,
  },
  {
    key: "content_aggregator",
    label: "Content Aggregator",
    layer: "L16",
    blurb: "Two related businesses on one shell — Catalog Aggregators (Shemaroo, Ultra, Divo) that acquire film libraries and syndicate to OTT platforms, and Music Aggregators (DistroKid, TuneCore, Believe, OneOrbit) that place indie tracks on Spotify · Apple Music · JioSaavn globally.",
    icon: Boxes,
    accent: "#B45309",
    cta: "Reserved · Coming Soon",
    href: "/onboard/content-aggregator",
    enabled: false,
  },
];

export default function Onboard() {
  return (
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12" data-testid="onboard-page">
      <OnboardBackButton fallbackTo="/" label="Back to Home" />
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Organization Onboarding</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
        Who&apos;s coming on board?
      </h1>
      <p className="mt-4 text-sm text-white/65 max-w-2xl leading-relaxed">
        CALL · SHEET starts with the organization. Once you&apos;re verified, you bring
        your team in — Producers, ADs, Line Producers, Accounts — each at their
        own authority level. Crew members join via your invite, not the public app.
      </p>

      <div className="mt-12 grid md:grid-cols-2 gap-5" data-testid="org-types-grid">
        {ORGS.map((o) => {
          const Icon = o.icon;
          return (
            <div key={o.key}
              className="cs-card rounded-2xl p-7 relative overflow-hidden group"
              data-testid={`org-tile-${o.key}`}
              style={{
                borderColor: `${o.accent}33`,
              }}
            >
              <div
                className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-10 blur-3xl pointer-events-none transition-opacity group-hover:opacity-20"
                style={{ background: o.accent }}
              />
              <div className="flex items-start gap-4 relative">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: `${o.accent}26`, color: o.accent }}
                >
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <h2 className="font-display text-2xl uppercase tracking-tight">{o.label}</h2>
                    {o.layer && (
                      <span
                        className="text-[9px] tracking-[0.24em] uppercase px-2 py-0.5 rounded-full border font-semibold"
                        style={{ color: o.accent, borderColor: `${o.accent}55`, background: `${o.accent}15` }}
                        data-testid={`org-tile-layer-${o.key}`}
                      >
                        {o.layer}
                      </span>
                    )}
                    {!o.enabled && (
                      <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full bg-white/10 text-white/55">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white/65 leading-relaxed">{o.blurb}</p>
                  {o.enabled ? (
                    <Link
                      to={o.href}
                      className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold transition-opacity hover:opacity-80"
                      style={{ color: o.accent }}
                      data-testid={`org-cta-${o.key}`}
                    >
                      {o.cta} <ArrowRight size={14} />
                    </Link>
                  ) : (
                    <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-white/40">
                      In private beta · Reach out
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 cs-card rounded-2xl p-6 flex items-start gap-4">
        <ShieldCheck size={20} className="text-emerald-400 shrink-0 mt-0.5" />
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Verification</div>
          <p className="text-sm text-white/75 mt-1.5 leading-relaxed">
            Every org goes through KYC before it can verify credentials or invite crew.
            For v1, we auto-approve so you can demo the flow end-to-end. Real
            verification (manual review + DigiLocker for PAN/GST) ships in v2.
          </p>
        </div>
      </div>
    </div>
  );
}
